"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect, useMemo, useRef } from "react";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { ActiveCheckoutPill } from "@/app/[tenant]/(console)/billing/check-out/persistence/active-checkout-pill";
import AccessExpiredScreen from "@/components/access-expired-screen";
import AccessExpiryBanner from "@/components/access-expiry-banner";
import AccessRestrictedScreen from "@/components/access-restricted-screen";
import { AskDecodaRunHost } from "@/components/ai-sidebar/ask-decoda-run-host";
import GlobalCommandPaletteModals from "@/components/command-palette-modals";
import GlobalChat from "@/components/global-chat";
import { ImportIssueBanner } from "@/components/import-review/import-issue-banner";
import ActiveInboundCall from "@/components/inbound-call/active-inbound-call";
import InboundCallBanner from "@/components/inbound-call/inbound-call-banner";
import { InboundCallProvider } from "@/components/inbound-call/inbound-call-provider";
import { SandboxBanner } from "@/components/layout/sandbox-banner";
import { TimezoneBanner } from "@/components/layout/timezone-banner";
import Loading from "@/components/loading";
import GlobalAppointmentLocationPrompt from "@/components/location/global-appointment-location-prompt";
import GlobalMemoWindow from "@/components/memo-window/global-memo-window";
import { NavigationSearch } from "@/components/navigation/navigation-search";
import { SidebarLayout } from "@/components/navigation/sidebar-layout";
import { PatientStatusOverdueToasts } from "@/components/patient-status-overdue-toasts";
import FloatingCallCard from "@/components/patient/call-modal/floating-call-card";
import { ProgressBar } from "@/components/progress-bar";
import PylonWidget from "@/components/pylon-widget";
import MicAccessDialog from "@/components/scribe/mic-access-dialog";
import RecordingPiP from "@/components/scribe/recording-pip";
import { SearchEdgeTab } from "@/components/search-fab/search-edge-tab";
import ServiceDisruptionBanner from "@/components/service-disruption-banner";
import { InPersonReturnGate } from "@/components/staff-pin/in-person-return-gate";
import { TrainingWatcher } from "@/components/training/training-watcher";
import { Background } from "@/components/ui/background";
import { VirtualServicesGuidePrompt } from "@/components/virtual-services-guide-prompt";
import { queryClient } from "@/config/react-query";
import { type SiteMapItem, getAllCommandBarRoutes, matchesRoutePattern } from "@/config/sitemap";
import { allTours } from "@/config/tours";
import { CommunicationProvider } from "@/contexts/chat-provider";
import { IsMobileProvider, useIsMobile } from "@/contexts/is-mobile-provider";
import { SystemToaster } from "@/contexts/system-toaster";
import TenantProvider, { useTenant } from "@/contexts/tenant-provider";
import { ThemeProvider } from "@/contexts/theme-provider";
import { TourOverlay, TourProvider } from "@/contexts/tour-provider";
import UserProvider, { useUser } from "@/contexts/user-provider";
import { WebSocketProvider } from "@/contexts/websocket-provider";
import { useDeskPhoneScreenPop } from "@/hooks/use-desk-phone-screen-pop";
import { useInAppNotifications } from "@/hooks/use-in-app-notifications";
import { useInPersonLocked } from "@/hooks/use-in-person-form-lock";
import { useModule } from "@/hooks/use-module";
import { useOnScreenKeyboardInset } from "@/hooks/use-on-screen-keyboard-inset";
import { useProviders } from "@/hooks/use-providers";
import { useRefetchOnTimezoneChange } from "@/hooks/use-refetch-on-timezone-change";
import { createChunkProcessor } from "@/hooks/use-scribe-transcription";
import { useSetupReadiness } from "@/hooks/use-setup-capability";
import { routeModulesEnabled } from "@/lib/module-gate";
import { hasAnyRole, hasPagePermissions, userIsAdmin } from "@/lib/permissions";
import { withReturnTo } from "@/lib/redirect-utils";
import { tenantMatchesAllowedSubDomains } from "@/lib/tenant-flags";
import { logTimestamp } from "@/lib/transcription-queue";
import { usePageViewTracking } from "@/lib/use-page-view-tracking";
import AccessDenied from "./access-denied";
import { useImportBannerInfo } from "./clinic-setup/hooks/use-import-banner";
import { ConsoleRealtimeCacheSubscribers } from "./console-realtime-cache-subscribers";
import InsufficientPermissions from "./insufficient-permissions";
import ModuleNotEnabled from "./module-not-enabled";
import { useTraining } from "@/api/provider/training/training-query";
import { selectIsExpired, useAccessExpiryStore } from "@/stores/access-expiry-store";
import { useScribeStore } from "@/stores/scribe-store";
import { useTranscriptionQueue } from "@/stores/transcription-queue-store";

/**
 * Initializes the transcription queue for fault-tolerant chunk delivery.
 * This component sets up the chunk processor and restores any pending
 * transcriptions from previous sessions.
 */
const TRANSCRIPTION_QUEUE_STALE_PRUNE_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12 hours

function TranscriptionQueueInitializer() {
  const $ = _c(3);
  const initialized = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (initialized.current) {
        return;
      }
      initialized.current = true;
      console.log(`[${logTimestamp()}][TranscriptionQueue] Initializing queue system...`);
      const scribeStore = useScribeStore.getState();
      const queue = useTranscriptionQueue.getState();
      const processor = createChunkProcessor({
        queryClient,
        setTranscriptionErrorNoteId: scribeStore.setTranscriptionErrorNoteId,
        setRecordingId: scribeStore.setRecordingId
      });
      queue.setProcessFn(processor);
      console.log(`[${logTimestamp()}][TranscriptionQueue] Chunk processor registered`);
      queue.initializeFromStorage().then(() => {
        console.log(`[${logTimestamp()}][TranscriptionQueue] Storage initialized, starting queue processor`);
        queue.processQueue();
      });
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(_temp2, t2);
  return null;
}
function _temp2() {
  const pruneIntervalId = setInterval(_temp, TRANSCRIPTION_QUEUE_STALE_PRUNE_INTERVAL_MS);
  return () => clearInterval(pruneIntervalId);
}
function _temp() {
  useTranscriptionQueue.getState().pruneStaleQueueData();
}
const NO_SIDEBAR_PAGES = ["/billing/quote", "/check-in/forms/fill", "/tip-screen", "/onboarding", "/clinic-setup"];
const ONBOARDING_PATH = "/onboarding";
const TRAINING_PATH = "/training";
type OnboardingStatusUser = {
  email?: string | null;
  onboardingStep?: "NOT_STARTED" | "PROFILE" | "THEME" | "CALENDAR_SYNC" | "CALENDAR_VIEW" | "NOTIFICATIONS" | "SECURITY" | "COMPLETE" | null | undefined;
  onboarding_step?: "NOT_STARTED" | "PROFILE" | "THEME" | "CALENDAR_SYNC" | "CALENDAR_VIEW" | "NOTIFICATIONS" | "SECURITY" | "COMPLETE" | null | undefined;
  onboardingSkippedAt?: string | null;
  onboarding_skipped_at?: string | null;
};

// Helper to check if onboarding is needed
function needsOnboarding(user: OnboardingStatusUser): boolean {
  // Skip for Decoda employees
  if (user.email?.endsWith("@decodahealth.com")) {
    return false;
  }

  // Skip if user has already skipped onboarding
  if (user.onboardingSkippedAt ?? user.onboarding_skipped_at) {
    return false;
  }

  // If onboardingStep is not set (null/undefined), treat as NOT_STARTED and show onboarding.
  // Handle both camelCase (from frontend types) and snake_case (from API response).
  const step = (user.onboardingStep ?? user.onboarding_step ?? "NOT_STARTED").toString().toUpperCase();

  // Need onboarding if not complete (includes NOT_STARTED and all other steps)
  return step !== "COMPLETE";
}
function AuthedComponent({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    currentUser
  } = useUser();
  const {
    tenant,
    isLoading: tenantIsLoading
  } = useTenant();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const {
    isMobile
  } = useIsMobile();
  const hasPatientStatusEnabled = useModule("CALENDAR_PATIENT_STATUS");
  const tenantSlug = params.tenant as string;

  // Current URL path+search — used as the returnTo when a guard bounces the
  // user to login/onboarding/clinic-setup so they can resume afterward.
  const searchString = searchParams?.toString() ?? "";
  const currentPathWithSearch = `${pathname}${searchString ? `?${searchString}` : ""}`;
  useEffect(() => {
    // Debounce redirect to prevent premature navigation during auth state transitions
    // The cleanup function handles cancellation when state changes, so no double-check needed
    if (currentUser === null || tenant === null && !tenantIsLoading) {
      const timeout = setTimeout(() => {
        // Preserve returnTo in both cases. If the user lacks access to the
        // tenant in the URL, the login-side isReturnToForTenant guard drops
        // the returnTo and sends them to their own tenant's home instead,
        // which breaks the loop that used to motivate stripping returnTo here.
        router.push(withReturnTo("/login", currentPathWithSearch));
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [currentUser, tenant, tenantIsLoading, router, currentPathWithSearch]);

  // Onboarding redirect logic
  useEffect(() => {
    if (!currentUser || tenantIsLoading || !tenant) return;
    const isOnboardingPage = pathname.includes(ONBOARDING_PATH);
    const shouldRedirect = needsOnboarding(currentUser);

    // DEBUG: Remove after fixing onboarding redirect
    if (process.env.NODE_ENV === "development") {
      const u = currentUser as {
        onboardingStep?: string;
        onboarding_step?: string;
      };
      console.log("[OnboardingRedirect]", {
        step: u.onboardingStep ?? u.onboarding_step,
        needsOnboarding: shouldRedirect,
        isOnboardingPage,
        willRedirect: shouldRedirect && !isOnboardingPage
      });
    }
    if (shouldRedirect && !isOnboardingPage) {
      router.push(withReturnTo(`/${tenantSlug}${ONBOARDING_PATH}`, currentPathWithSearch));
    }
  }, [currentUser, tenant, tenantIsLoading, pathname, router, tenantSlug, currentPathWithSearch]);

  // Check if user has access to current route. Use the unfiltered sitemap so
  // module-gated routes still match — we then explicitly distinguish "module
  // not enabled for this tenant" from a true 404 or a permissions failure.
  const pathAfterTenant = pathname.substring(pathname.indexOf("/", pathname.indexOf("/") + 1));
  // Match the primary route OR any registered alias, so an alias-only entry
  // still inherits the same module/permission gate. Without this, opening an alias directly falls
  // through to the default-allow branch below and bypasses the gate.
  const currentPage = useMemo(() => {
    const matches = getAllCommandBarRoutes().filter((route: SiteMapItem) => matchesRoutePattern(route.route, pathAfterTenant) || route.routeAliases?.some(alias => matchesRoutePattern(alias, pathAfterTenant)));
    // A fixed destination outranks a param pattern that also happens to match,
    // the way Next.js prefers a static segment over `[param]`. Without this a
    // built-in dashboard could be gated by the custom-dashboard pattern.
    return matches.find(route_0 => !route_0.route?.includes(":")) ?? matches[0];
  }, [pathAfterTenant]);
  const {
    data: setupReadiness,
    isPending: setupReadinessPending
  } = useSetupReadiness();

  // Hold an admin in the wizard until they finish it — BILLING lives in there
  // and it's the only place a clinic can give us a card. `setupLocked` is
  // already false for legacy tenants, past the go-live window, and finished.
  useEffect(() => {
    if (!currentUser || tenantIsLoading || !tenant) return;
    // Provider onboarding owns them first.
    if (needsOnboarding(currentUser)) return;
    if (pathname.includes("/clinic-setup")) return;
    if (pathname.includes(ONBOARDING_PATH)) return;
    // Staff shouldn't lose the app because an owner hasn't added a card.
    if (!userIsAdmin(currentUser)) return;
    // Still loading; treating that as locked would bounce every cold load.
    if (setupReadiness === undefined) return;
    // Mid tenant-switch both `tenant` and readiness still describe the previous
    // clinic for a render. Acting on that redirects into the new tenant's
    // wizard on the old tenant's lock state.
    if (tenant.subDomain !== tenantSlug) return;
    // Fail open on error.
    if (!setupReadiness?.progress.setupLocked) return;
    router.push(withReturnTo(`/${tenantSlug}/clinic-setup`, currentPathWithSearch));
  }, [currentUser, tenant, tenantIsLoading, pathname, router, tenantSlug, currentPathWithSearch, setupReadiness]);
  const {
    data: training
  } = useTraining();

  // In a sandbox, send someone who hasn't picked a training role to do so, once their
  // own onboarding and any clinic setup the wizard is holding them in are out of the way.
  useEffect(() => {
    if (!currentUser || tenantIsLoading || !tenant) return;
    if (tenant.subDomain !== tenantSlug) return;
    if (needsOnboarding(currentUser)) return;
    if (currentUser.email?.endsWith("@decodahealth.com")) return;
    if (!training?.available || training.plan) return;
    // Wait for readiness, or an admin the wizard is about to claim lands here first.
    if (userIsAdmin(currentUser) && (setupReadinessPending || setupReadiness?.progress.setupLocked)) return;
    if ([TRAINING_PATH, ONBOARDING_PATH, "/clinic-setup"].some(path => pathname.includes(path))) {
      return;
    }
    router.push(`/${tenantSlug}${TRAINING_PATH}`);
  }, [currentUser, tenant, tenantIsLoading, tenantSlug, training, setupReadiness, setupReadinessPending, pathname, router]);
  let hasAccess = false;
  let accessError: "role" | "permission" | "module" | null = null;
  if (!currentPage && pathname.includes("dashboard")) {
    hasAccess = false;
    accessError = "role";
  } else if (currentPage) {
    // Check module enablement first — if the tenant doesn't have the module,
    // role/permission checks are moot and we want a clear "not enabled" screen.
    const enabledModules = tenant?.enabledModules ?? [];
    const moduleEnabled = routeModulesEnabled(currentPage, enabledModules);

    // Check role requirements (existing logic)
    const hasRequiredRoles = !currentPage.roles || hasAnyRole(currentUser, currentPage.roles);

    // Check permission requirements (new logic)
    const hasRequiredPermissions = hasPagePermissions(currentUser, currentPage);

    // Destinations restricted to named tenants. Enforced here so the sitemap
    // entry is the only place that says who may open a route.
    const visibleToTenant = tenantMatchesAllowedSubDomains({
      tenantSubDomain: tenant?.subDomain,
      allowedSubDomains: currentPage.tenantSubDomains,
      includeAllInDevelopment: false
    });
    hasAccess = moduleEnabled && visibleToTenant && hasRequiredRoles && hasRequiredPermissions;

    // Determine which type of error to show
    if (!hasAccess) {
      if (!moduleEnabled || !visibleToTenant) {
        accessError = "module";
      } else if (!hasRequiredRoles) {
        accessError = "role";
      } else if (!hasRequiredPermissions) {
        accessError = "permission";
      }
    }
  } else {
    // No page configuration found, allow access by default
    hasAccess = true;
  }
  const baseLoading = currentUser === undefined || currentUser === null || tenantIsLoading || !tenant;

  // Keep the in-person form subtree mounted through a TRANSIENT auth gap. The
  // form runs in its own tab and a patient may be partway through it; the auth
  // listener can briefly resolve `currentUser` to null during a token refresh or
  // a refetch, and unmounting the subtree then would wipe their in-progress
  // answers. The login redirect below is already debounced 500ms for exactly
  // this transition, so we extend the same grace to the mount: while we're on
  // the fill route, the tenant is still resolved, and only the user momentarily
  // dropped out, hold the subtree mounted (the fill layout renders a skeleton or
  // its latched form for a null user). If the gap persists, the debounced
  // redirect fires and navigation unmounts everything anyway.
  const onInPersonFill = pathname.includes("/check-in/forms/fill");
  const fillWasReadyRef = useRef(false);
  if (!onInPersonFill) fillWasReadyRef.current = false;else if (!baseLoading) fillWasReadyRef.current = true;
  const holdFillThroughAuthGap = onInPersonFill && fillWasReadyRef.current && currentUser == null &&
  // the only missing piece is the user
  !tenantIsLoading && !!tenant;
  const isLoading = baseLoading && !holdFillThroughAuthGap;
  // formatStaffName reads each staff member's name display choice from this
  // list in the query cache, so the page waits for it before drawing names.
  // Wait only for the list's first answer. A refetch of a list that failed
  // with no data goes back to `pending`, so gating on `isLoading` alone would
  // unmount the console, and remounting it starts the next refetch (the right
  // sidebar reads this list): a request loop that trips the client rate limit.
  const {
    isLoading: staffQueryLoading,
    isFetched: staffFetched
  } = useProviders({
    showArchived: true,
    queryOptions: {
      enabled: !isLoading
    }
  });
  const staffLoading = staffQueryLoading && !staffFetched;

  // While a patient holds the device for an in-person form, nothing that leads
  // back into staff screens (support chat, parked carts) stays on screen.
  const inPersonLocked = useInPersonLocked();
  const setExpiresAt = useAccessExpiryStore(s => s.setExpiresAt);
  const storeIsAccessExpired = useAccessExpiryStore(selectIsExpired);
  useEffect(() => {
    setExpiresAt(currentUser?.expiresAt ?? null);
  }, [currentUser?.expiresAt, setExpiresAt]);

  // Compute synchronously from currentUser so the very first render gates
  // expired users; the store value keeps us reactive once the ticker advances.
  const expiresAt = currentUser?.expiresAt ? new Date(currentUser.expiresAt) : null;
  const isAccessExpired = storeIsAccessExpired || expiresAt !== null && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() <= Date.now();
  const isAccessRestricted = currentUser?.accessRestrictedUntil != null && new Date(currentUser.accessRestrictedUntil).getTime() > Date.now();
  if (currentUser && isAccessExpired) {
    return <AccessExpiredScreen />;
  }
  if (currentUser && isAccessRestricted) {
    return <AccessRestrictedScreen until={new Date(currentUser.accessRestrictedUntil!)} />;
  }
  return <>
      <Loading isVisible={isLoading || staffLoading} />
      {!isLoading && !staffLoading && <WebSocketProvider key={tenant?.subDomain}>
          <TranscriptionQueueInitializer />
          <InAppNotificationHandler />
          <ConsoleRealtimeCacheSubscribers />
          <DeskPhoneScreenPopSubscriber />
          {!pathname.includes("/tip-screen") && !inPersonLocked && !currentUser?.hideSupportChat && <PylonWidget />}
          {!pathname.includes("/tip-screen") && !inPersonLocked && <ActiveCheckoutPill />}
          {hasPatientStatusEnabled && <PatientStatusOverdueToasts />}
          <InboundCallProvider>
            <CommunicationProvider>
              <TourProvider tours={allTours}>
                <TrainingWatcher />
                {NO_SIDEBAR_PAGES.some(page => pathname.includes(page)) ? <main className="flex h-full w-full flex-col overflow-y-auto">
                    {children}
                  </main> : <>
                    <SearchEdgeTab />
                    <SidebarLayout>
                      <main className="flex h-full w-full flex-col overflow-y-auto">
                        <TourOverlay tours={allTours} />
                        <ServiceDisruptionBanner />
                        <AccessExpiryBanner />
                        <SandboxBanner />
                        {/* Zero-height anchor so the banner overlays the page instead
                            of pushing it down (which made full-height pages scroll),
                            while starting below the alerts above it. */}
                        <div className="relative z-40 h-0 shrink-0">
                          <TimezoneBanner className="absolute inset-x-0 top-0 shadow-md" />
                        </div>
                        <SystemToaster />
                        <FloatingCallCard />
                        <InboundCallBanner />
                        <ActiveInboundCall />
                        <RecordingPiP />
                        <VirtualServicesGuidePrompt />
                        <MicAccessDialog />
                        <GlobalAppointmentLocationPrompt />

                        {hasAccess ? children : accessError === "module" ? <ModuleNotEnabled /> : accessError === "permission" ? <InsufficientPermissions requiredPermissions={[...(currentPage?.permissions ?? []), ...(currentPage?.allPermissions ?? [])]} requiredRoles={currentPage?.roles} /> : <AccessDenied />}
                      </main>
                      {tenant?.enabledModules?.includes("TEXTING") && !isMobile && <GlobalChat />}
                    </SidebarLayout>
                  </>}
                <GlobalCommandPaletteModals />
                <GlobalMemoWindow />
              </TourProvider>
            </CommunicationProvider>
          </InboundCallProvider>
        </WebSocketProvider>}
    </>;
}
function PageViewTracker() {
  usePageViewTracking();
  return null;
}
function InAppNotificationHandler() {
  useInAppNotifications();
  return null;
}
function DeskPhoneScreenPopSubscriber() {
  useDeskPhoneScreenPop();
  return null;
}
function ImportBannerWrapper() {
  const $ = _c(3);
  const info = useImportBannerInfo();
  if (!info) {
    return null;
  }
  let t0;
  if ($[0] !== info.category || $[1] !== info.sourceSystem) {
    t0 = <ImportIssueBanner sourceSystem={info.sourceSystem} category={info.category} />;
    $[0] = info.category;
    $[1] = info.sourceSystem;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export default function ConsoleLayoutClient(t0) {
  const $ = _c(12);
  const {
    children
  } = t0;
  useRefetchOnTimezoneChange(queryClient);
  useOnScreenKeyboardInset();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Background />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AskDecodaRunHost />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <NavigationSearch />;
    t4 = <PageViewTracker />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children) {
    t5 = <AuthedComponent>{children}</AuthedComponent>;
    $[4] = children;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <InPersonReturnGate />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <UserProvider><TenantProvider>{t2}<ProgressBar className="fixed top-0 z-50 h-1 bg-primary">{t3}{t4}{t5}{t6}</ProgressBar></TenantProvider></UserProvider>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_TANSTACK_DEVTOOLS === "true" && <ReactQueryDevtools initialIsOpen={false} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = <div className="flex h-[calc(100dvh-var(--keyboard-inset,0px))] w-full overflow-hidden"><ThemeProvider>{t1}<IsMobileProvider><QueryClientProvider client={queryClient}>{t7}{t8}</QueryClientProvider></IsMobileProvider></ThemeProvider></div>;
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}